<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { dynamicTime } from 'shared/helpers/timeHelper';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import ContactInfoRow from './ContactInfoRow.vue';
import Avatar from 'next/avatar/Avatar.vue';
import SocialIcons from './SocialIcons.vue';
import EditContact from './EditContact.vue';
import QuickChannelLauncher from './QuickChannelLauncher.vue';
import ContactMergeModal from 'dashboard/modules/contact/ContactMergeModal.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ContactLabels from 'dashboard/components-next/Contacts/ContactLabels/ContactLabels.vue';
import VoiceCallButton from 'dashboard/components-next/Contacts/VoiceCallButton.vue';

import {
  isAConversationRoute,
  isAInboxViewRoute,
  getConversationDashboardRoute,
} from '../../../../helper/routeHelpers';

export default {
  components: {
    NextButton,
    ContactInfoRow,
    EditContact,
    QuickChannelLauncher,
    Avatar,
    SocialIcons,
    ContactMergeModal,
    ContactLabels,
    VoiceCallButton,
  },
  props: {
    contact: {
      type: Object,
      default: () => ({}),
    },
    showAvatar: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['panelClose'],
  setup() {
    const { isAdmin } = useAdmin();
    return {
      isAdmin,
    };
  },
  data() {
    return {
      showEditModal: false,
      showDeleteModal: false,
    };
  },
  computed: {
    ...mapGetters({ uiFlags: 'contacts/getUIFlags' }),
    contactProfileLink() {
      return `/app/accounts/${this.$route.params.accountId}/contacts/${this.contact.id}`;
    },
    additionalAttributes() {
      return this.contact.additional_attributes || {};
    },
    location() {
      const {
        country = '',
        city = '',
        country_code: countryCode,
      } = this.additionalAttributes;
      const cityAndCountry = [city, country].filter(item => !!item).join(', ');

      if (!cityAndCountry) {
        return '';
      }
      return this.findCountryFlag(countryCode, cityAndCountry);
    },
    socialProfiles() {
      const {
        social_profiles: socialProfiles,
        screen_name: twitterScreenName,
        social_telegram_user_name: telegramUsername,
      } = this.additionalAttributes;

      const telegram = socialProfiles?.telegram || telegramUsername || '';
      const twitter = socialProfiles?.twitter || twitterScreenName || '';

      return {
        ...(socialProfiles || {}),
        twitter,
        telegram,
      };
    },
    // DV: account 1 gets an explicit row per channel instead of tiny icons.
    isDvAccount() {
      return Number(this.$route.params.accountId) === 1;
    },
    dvCreatedDate() {
      const raw = Number(this.contact.created_at);
      if (!raw) return '';
      const date = new Date(raw < 1e12 ? raw * 1000 : raw);
      return new Intl.DateTimeFormat(this.$i18n.locale || 'ru', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      }).format(date);
    },
    dvContactRows() {
      const rows = [];
      const phone = this.contact.phone_number;
      if (phone) {
        const digits = String(phone).replace(/\D/g, '');
        rows.push({
          key: 'phone',
          icon: 'i-lucide-phone',
          label: this.$t('CONTACT_PANEL.DV_CONTACT.PHONE'),
          value: phone,
          href: `tel:${phone}`,
          copy: phone,
          extra: digits
            ? {
                href: `https://wa.me/${digits}`,
                icon: 'i-ri-whatsapp-line',
                title: this.$t('CONTACT_PANEL.DV_CONTACT.WHATSAPP'),
              }
            : null,
        });
      }
      if (this.contact.email) {
        rows.push({
          key: 'email',
          icon: 'i-lucide-mail',
          label: this.$t('CONTACT_PANEL.DV_CONTACT.EMAIL'),
          value: this.contact.email,
          href: `mailto:${this.contact.email}`,
          copy: this.contact.email,
        });
      }

      const attrs = this.additionalAttributes;
      const profiles = this.socialProfiles;
      const socials = [
        {
          key: 'instagram',
          icon: 'i-ri-instagram-line',
          handle: profiles.instagram || attrs.social_instagram_user_name,
          url: name => `https://instagram.com/${name}`,
        },
        {
          key: 'telegram',
          icon: 'i-ri-telegram-line',
          handle: profiles.telegram || attrs.social_telegram_user_name,
          url: name => `https://t.me/${name}`,
        },
        {
          key: 'facebook',
          icon: 'i-ri-facebook-circle-line',
          handle: profiles.facebook,
          url: name => `https://facebook.com/${name}`,
        },
        {
          key: 'tiktok',
          icon: 'i-ri-tiktok-line',
          handle: profiles.tiktok,
          url: name => `https://tiktok.com/@${name}`,
        },
        {
          key: 'linkedin',
          icon: 'i-ri-linkedin-box-line',
          handle: profiles.linkedin,
          url: name => `https://linkedin.com/in/${name}`,
        },
        {
          key: 'twitter',
          icon: 'i-ri-twitter-x-line',
          handle: profiles.twitter,
          url: name => `https://x.com/${name}`,
        },
        {
          key: 'github',
          icon: 'i-ri-github-line',
          handle: profiles.github,
          url: name => `https://github.com/${name}`,
        },
      ];
      socials.forEach(social => {
        const raw = String(social.handle || '').trim();
        if (!raw) return;
        const isUrl = /^https?:\/\//i.test(raw);
        const handle = isUrl
          ? raw.replace(/\/+$/, '').split('/').pop().replace(/^@/, '')
          : raw.replace(/^@/, '');
        if (!handle) return;
        rows.push({
          key: social.key,
          icon: social.icon,
          label: this.$t(`CONTACT_PANEL.DV_CONTACT.${social.key.toUpperCase()}`),
          value: `@${handle}`,
          href: isUrl ? raw : social.url(handle),
          external: true,
          copy: `@${handle}`,
        });
      });

      if (attrs.company_name) {
        rows.push({
          key: 'company',
          icon: 'i-lucide-building-2',
          label: this.$t('CONTACT_PANEL.DV_CONTACT.COMPANY'),
          value: attrs.company_name,
        });
      }
      const place = [attrs.city, attrs.country].filter(Boolean).join(', ');
      if (place || attrs.location) {
        rows.push({
          key: 'location',
          icon: 'i-lucide-map-pin',
          label: this.$t('CONTACT_PANEL.DV_CONTACT.LOCATION'),
          value: place || attrs.location,
        });
      }
      return rows;
    },
    // Delete Modal
    confirmDeleteMessage() {
      return ` ${this.contact.name}?`;
    },
  },
  watch: {
    'contact.id': {
      handler(id) {
        if (!id) {
          return;
        }

        this.$store.dispatch('contacts/fetchContactableInbox', id);
        this.$store.dispatch('contactConversations/get', id);
      },
      immediate: true,
    },
  },
  methods: {
    dynamicTime,
    toggleEditModal() {
      this.showEditModal = !this.showEditModal;
    },
    toggleDeleteModal() {
      this.showDeleteModal = !this.showDeleteModal;
    },
    confirmDeletion() {
      this.deleteContact(this.contact);
      this.closeDelete();
    },
    closeDelete() {
      this.showDeleteModal = false;
      this.showEditModal = false;
    },
    findCountryFlag(countryCode, cityAndCountry) {
      try {
        if (!countryCode) {
          return `${cityAndCountry} 🌎`;
        }

        const code = countryCode?.toLowerCase();
        return `${cityAndCountry} <span class="fi fi-${code} size-3.5"></span>`;
      } catch (error) {
        return '';
      }
    },
    async deleteContact({ id }) {
      try {
        await this.$store.dispatch('contacts/delete', id);
        this.$emit('panelClose');
        useAlert(this.$t('DELETE_CONTACT.API.SUCCESS_MESSAGE'));

        if (isAConversationRoute(this.$route.name)) {
          this.$router.push({
            name: getConversationDashboardRoute(this.$route.name),
          });
        } else if (isAInboxViewRoute(this.$route.name)) {
          this.$router.push({
            name: 'inbox_view',
          });
        } else if (this.$route.name !== 'contacts_dashboard') {
          this.$router.push({
            name: 'contacts_dashboard',
          });
        }
      } catch (error) {
        useAlert(
          error.message
            ? error.message
            : this.$t('DELETE_CONTACT.API.ERROR_MESSAGE')
        );
      }
    },
    async copyDvValue(value) {
      try {
        await copyTextToClipboard(value);
        useAlert(this.$t('CONTACT_PANEL.DV_CONTACT.COPIED'));
      } catch (error) {
        // Clipboard can be blocked in some webviews; nothing else to do.
      }
    },
  },
};
</script>

<template>
  <div v-if="isDvAccount" class="relative w-full px-4 pb-3 pt-4">
    <div class="flex items-center gap-3">
      <Avatar
        v-if="showAvatar"
        :src="contact.thumbnail"
        :name="contact.name"
        :status="contact.availability_status"
        :size="44"
        hide-offline-status
        rounded-full
      />
      <div class="min-w-0 flex-1">
        <div class="flex min-w-0 items-center gap-1.5">
          <h3
            class="my-0 min-w-0 truncate text-base font-semibold capitalize text-n-slate-12"
            :title="contact.name"
          >
            {{ contact.name }}
          </h3>
          <a
            :href="contactProfileLink"
            target="_blank"
            rel="noopener nofollow noreferrer"
            class="shrink-0 leading-3"
            :title="$t('CONTACT_PANEL.DV_CONTACT.OPEN')"
          >
            <span class="i-lucide-external-link text-sm text-n-slate-10" />
          </a>
        </div>
        <p v-if="dvCreatedDate" class="mb-0 mt-0.5 text-xs text-n-slate-10">
          {{
            $t('CONTACT_PANEL.DV_CONTACT.CREATED', {
              time: dvCreatedDate,
            })
          }}
        </p>
      </div>
    </div>

    <p
      v-if="additionalAttributes.description"
      class="mb-0 mt-2 break-words text-sm text-n-slate-11"
    >
      {{ additionalAttributes.description }}
    </p>

    <div
      v-if="dvContactRows.length"
      class="mt-3 overflow-hidden rounded-xl border border-n-weak"
    >
      <div
        v-for="row in dvContactRows"
        :key="row.key"
        class="flex items-center gap-2.5 border-b border-n-weak px-3 py-2 last:border-b-0"
      >
        <span :class="row.icon" class="shrink-0 text-base text-n-slate-11" />
        <div class="min-w-0 flex-1">
          <p class="mb-0 text-[11px] leading-4 text-n-slate-10">
            {{ row.label }}
          </p>
          <a
            v-if="row.href"
            :href="row.href"
            :target="row.external ? '_blank' : undefined"
            rel="noopener noreferrer"
            class="block truncate text-sm leading-5 text-n-slate-12 hover:text-n-brand hover:underline"
            :title="row.value"
          >
            {{ row.value }}
          </a>
          <p
            v-else
            class="mb-0 truncate text-sm leading-5 text-n-slate-12"
            :title="row.value"
          >
            {{ row.value }}
          </p>
        </div>
        <a
          v-if="row.extra"
          :href="row.extra.href"
          target="_blank"
          rel="noopener noreferrer"
          class="flex size-7 shrink-0 items-center justify-center rounded-lg text-n-teal-11 hover:bg-n-alpha-2"
          :title="row.extra.title"
        >
          <span :class="row.extra.icon" class="text-base" />
        </a>
        <a
          v-if="row.external"
          :href="row.href"
          target="_blank"
          rel="noopener noreferrer"
          class="flex size-7 shrink-0 items-center justify-center rounded-lg text-n-slate-11 hover:bg-n-alpha-2"
          :title="$t('CONTACT_PANEL.DV_CONTACT.OPEN')"
        >
          <span class="i-lucide-arrow-up-right text-base" />
        </a>
        <button
          v-if="row.copy"
          type="button"
          class="flex size-7 shrink-0 items-center justify-center rounded-lg text-n-slate-11 hover:bg-n-alpha-2"
          :title="$t('CONTACT_PANEL.DV_CONTACT.COPY')"
          @click="copyDvValue(row.copy)"
        >
          <span class="i-lucide-copy shrink-0 !w-4 text-base" />
        </button>
      </div>
    </div>
    <p
      v-else
      class="mb-0 mt-3 rounded-xl border border-dashed border-n-weak px-3 py-2 text-xs text-n-slate-10"
    >
      {{ $t('CONTACT_PANEL.DV_CONTACT.NO_DETAILS') }}
    </p>

    <div v-if="contact.id" class="mt-2.5 w-full">
      <ContactLabels :contact-id="contact.id" />
    </div>

    <div class="mt-3 flex items-center gap-2">
      <QuickChannelLauncher :contact="contact" compact />
      <div class="ml-auto flex items-center gap-1.5">
        <VoiceCallButton
          :phone="contact.phone_number"
          :contact-id="contact.id"
          icon="i-ri-phone-fill"
          size="sm"
          :tooltip-label="$t('CONTACT_PANEL.CALL')"
          slate
          faded
        />
        <NextButton
          v-tooltip.top-end="$t('EDIT_CONTACT.BUTTON_LABEL')"
          icon="i-ph-pencil-simple"
          slate
          faded
          sm
          @click="toggleEditModal"
        />
        <ContactMergeModal :primary-contact="contact">
          <template #trigger>
            <NextButton
              v-tooltip.top-end="$t('CONTACT_PANEL.MERGE_CONTACT')"
              icon="i-ph-arrows-merge"
              slate
              faded
              sm
              :disabled="uiFlags.isMerging"
            />
          </template>
        </ContactMergeModal>
        <NextButton
          v-if="isAdmin"
          v-tooltip.top-end="$t('DELETE_CONTACT.BUTTON_LABEL')"
          icon="i-ph-trash"
          slate
          faded
          sm
          ruby
          :disabled="uiFlags.isDeleting"
          @click="toggleDeleteModal"
        />
      </div>
    </div>
    <EditContact
      v-if="showEditModal"
      :show="showEditModal"
      :contact="contact"
      @cancel="toggleEditModal"
    />
    <woot-delete-modal
      v-if="showDeleteModal"
      v-model:show="showDeleteModal"
      :on-close="closeDelete"
      :on-confirm="confirmDeletion"
      :title="$t('DELETE_CONTACT.CONFIRM.TITLE')"
      :message="$t('DELETE_CONTACT.CONFIRM.MESSAGE')"
      :message-value="confirmDeleteMessage"
      :confirm-text="$t('DELETE_CONTACT.CONFIRM.YES')"
      :reject-text="$t('DELETE_CONTACT.CONFIRM.NO')"
    />
  </div>
  <div v-else class="relative items-center w-full p-4">
    <div class="flex flex-col w-full gap-2 text-left rtl:text-right">
      <div class="flex flex-row justify-between">
        <Avatar
          v-if="showAvatar"
          :src="contact.thumbnail"
          :name="contact.name"
          :status="contact.availability_status"
          :size="48"
          hide-offline-status
          rounded-full
        />
      </div>

      <div class="flex flex-col items-start gap-1.5 min-w-0 w-full">
        <div v-if="showAvatar" class="flex items-center w-full min-w-0 gap-3">
          <h3
            class="flex-shrink max-w-full min-w-0 my-0 text-base capitalize break-words text-n-slate-12"
          >
            {{ contact.name }}
          </h3>
          <div class="flex flex-row items-center gap-2">
            <span
              v-if="contact.created_at"
              v-tooltip.left="
                `${$t('CONTACT_PANEL.CREATED_AT_LABEL')} ${dynamicTime(
                  contact.created_at
                )}`
              "
              class="i-lucide-info text-sm text-n-slate-10"
            />
            <a
              :href="contactProfileLink"
              target="_blank"
              rel="noopener nofollow noreferrer"
              class="leading-3"
            >
              <span class="i-lucide-external-link text-sm text-n-slate-10" />
            </a>
          </div>
        </div>

        <p v-if="additionalAttributes.description" class="break-words mb-0.5">
          {{ additionalAttributes.description }}
        </p>
        <div class="flex flex-col items-start w-full gap-2">
          <ContactInfoRow
            :href="contact.email ? `mailto:${contact.email}` : ''"
            :value="contact.email"
            icon="mail"
            emoji="✉️"
            :title="$t('CONTACT_PANEL.EMAIL_ADDRESS')"
            show-copy
          />
          <ContactInfoRow
            :href="contact.phone_number ? `tel:${contact.phone_number}` : ''"
            :value="contact.phone_number"
            icon="call"
            emoji="📞"
            :title="$t('CONTACT_PANEL.PHONE_NUMBER')"
            show-copy
          />
          <ContactInfoRow
            v-if="contact.identifier"
            :value="contact.identifier"
            icon="contact-identify"
            emoji="🪪"
            :title="$t('CONTACT_PANEL.IDENTIFIER')"
          />
          <ContactInfoRow
            :value="additionalAttributes.company_name"
            icon="building-bank"
            emoji="🏢"
            :title="$t('CONTACT_PANEL.COMPANY')"
          />
          <ContactInfoRow
            v-if="location || additionalAttributes.location"
            :value="location || additionalAttributes.location"
            icon="map"
            emoji="🌍"
            :title="$t('CONTACT_PANEL.LOCATION')"
          />
          <SocialIcons :social-profiles="socialProfiles" />
          <div v-if="contact.id" class="w-full pt-1">
            <ContactLabels :contact-id="contact.id" />
          </div>
        </div>
      </div>
      <div class="flex flex-wrap items-center w-full mt-0.5 gap-2">
        <QuickChannelLauncher :contact="contact" />
        <VoiceCallButton
          :phone="contact.phone_number"
          :contact-id="contact.id"
          icon="i-ri-phone-fill"
          size="sm"
          :tooltip-label="$t('CONTACT_PANEL.CALL')"
          slate
          faded
        />
        <NextButton
          v-tooltip.top-end="$t('EDIT_CONTACT.BUTTON_LABEL')"
          icon="i-ph-pencil-simple"
          slate
          faded
          sm
          @click="toggleEditModal"
        />
        <ContactMergeModal :primary-contact="contact">
          <template #trigger>
            <NextButton
              v-tooltip.top-end="$t('CONTACT_PANEL.MERGE_CONTACT')"
              icon="i-ph-arrows-merge"
              slate
              faded
              sm
              :disabled="uiFlags.isMerging"
            />
          </template>
        </ContactMergeModal>
        <NextButton
          v-if="isAdmin"
          v-tooltip.top-end="$t('DELETE_CONTACT.BUTTON_LABEL')"
          icon="i-ph-trash"
          slate
          faded
          sm
          ruby
          :disabled="uiFlags.isDeleting"
          @click="toggleDeleteModal"
        />
      </div>
      <EditContact
        v-if="showEditModal"
        :show="showEditModal"
        :contact="contact"
        @cancel="toggleEditModal"
      />
    </div>
    <woot-delete-modal
      v-if="showDeleteModal"
      v-model:show="showDeleteModal"
      :on-close="closeDelete"
      :on-confirm="confirmDeletion"
      :title="$t('DELETE_CONTACT.CONFIRM.TITLE')"
      :message="$t('DELETE_CONTACT.CONFIRM.MESSAGE')"
      :message-value="confirmDeleteMessage"
      :confirm-text="$t('DELETE_CONTACT.CONFIRM.YES')"
      :reject-text="$t('DELETE_CONTACT.CONFIRM.NO')"
    />
  </div>
</template>
